import React, { useState, useEffect } from "react";
import { FaCheckCircle, FaTimesCircle } from "react-icons/fa";
import { formatBDT } from "../Courses/types/formatCurrency";

interface InstallmentPaymentModalProps {
  isOpen: boolean;
  onClose: () => void;
  installmentId: number;
  onPaymentSuccess: () => void;
}

interface InstallmentDetails {
  installmentId: number;
  sequenceNumber: number;
  amount: number;
  dueDate: string;
  courseName: string;
  studentName: string;
  canPay: boolean;
}

const InstallmentPaymentModal: React.FC<InstallmentPaymentModalProps> = ({
  isOpen,
  onClose,
  installmentId,
  onPaymentSuccess,
}) => {
  const [details, setDetails] = useState<InstallmentDetails | null>(null);
  const [paymentMethod, setPaymentMethod] = useState<"bkash" | "bank">("bkash");
  const [paymentSlip, setPaymentSlip] = useState<File | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [confirmationType, setConfirmationType] = useState<"success" | "error">(
    "success"
  );
  const [confirmationMessage, setConfirmationMessage] = useState("");

  const [paymentData, setPaymentData] = useState({
    transactionId: "",
    bankName: "",
    branchName: "",
    referenceNumber: "",
    paymentDate: new Date().toISOString().split("T")[0],
  });

  useEffect(() => {
    if (isOpen && installmentId) {
      fetchInstallmentDetails();
    }
  }, [isOpen, installmentId]);

  const fetchInstallmentDetails = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/student/installments/${installmentId}/details`,
        { credentials: "include" }
      );

      if (!response.ok) throw new Error("Failed to fetch installment details");

      const data = await response.json();
      setDetails(data);
    } catch (err) {
      setError("Failed to load installment details");
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file size (2MB)
      if (file.size > 2 * 1024 * 1024) {
        setError("File size must be less than 2MB");
        setPaymentSlip(null);
        return;
      }
      // Validate file type
      const validTypes = [
        "application/pdf",
        "image/jpeg",
        "image/jpg",
        "image/png",
      ];
      if (!validTypes.includes(file.type)) {
        setError("Only PDF and image files (JPG, PNG) are allowed");
        setPaymentSlip(null);
        return;
      }
      setPaymentSlip(file);
      setError("");
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");

    // Validate payment slip
    if (!paymentSlip) {
      setError("Payment slip is required. Please upload your payment receipt.");
      setSubmitting(false);
      return;
    }

    // Validation
    if (paymentMethod === "bkash" && !paymentData.transactionId) {
      setError("Transaction ID is required for bKash payments");
      setSubmitting(false);
      return;
    }

    if (
      paymentMethod === "bank" &&
      (!paymentData.bankName || !paymentData.referenceNumber)
    ) {
      setError("Bank details are required for bank transfers");
      setSubmitting(false);
      return;
    }

    try {
      // Create FormData
      const formData = new FormData();

      // Add payment data as JSON
      const paymentPayload = {
        paymentMethod,
        ...paymentData,
      };

      formData.append(
        "payment",
        new Blob([JSON.stringify(paymentPayload)], {
          type: "application/json",
        })
      );

      // Add file
      formData.append("paymentSlip", paymentSlip);

      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/student/installments/${installmentId}/pay`,
        {
          method: "POST",
          credentials: "include",
          body: formData,
        }
      );

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || "Payment submission failed");
      }

      // Show success confirmation
      setConfirmationType("success");
      setConfirmationMessage(
        result.message ||
          "Payment submitted successfully! We'll verify and update your installment status shortly."
      );
      setShowConfirmation(true);
    } catch (err: any) {
      // Show error confirmation
      setConfirmationType("error");
      setConfirmationMessage(
        err.message || "Payment submission failed. Please try again."
      );
      setShowConfirmation(true);
    } finally {
      setSubmitting(false);
    }
  };

  const handleConfirmationClose = () => {
    setShowConfirmation(false);
    if (confirmationType === "success") {
      onPaymentSuccess();
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto relative">
        {/* Confirmation Dialog */}
        {showConfirmation && (
          <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[10000] rounded-lg">
            <div className="bg-white rounded-lg shadow-xl max-w-sm w-full mx-4">
              <div className="p-6 text-center">
                <div className="mb-4">
                  {confirmationType === "success" ? (
                    <FaCheckCircle className="mx-auto text-6xl text-green-500" />
                  ) : (
                    <FaTimesCircle className="mx-auto text-6xl text-red-500" />
                  )}
                </div>
                <h3
                  className={`text-lg font-semibold mb-3 ${
                    confirmationType === "success"
                      ? "text-green-800"
                      : "text-red-800"
                  }`}
                >
                  {confirmationType === "success"
                    ? "Payment Submitted!"
                    : "Payment Failed"}
                </h3>
                <p className="text-gray-700 mb-6 leading-relaxed">
                  {confirmationMessage}
                </p>
                <button
                  onClick={handleConfirmationClose}
                  className={`w-full px-4 py-2 rounded-lg font-semibold transition-colors ${
                    confirmationType === "success"
                      ? "bg-green-600 text-white hover:bg-green-700"
                      : "bg-red-600 text-white hover:bg-red-700"
                  }`}
                >
                  {confirmationType === "success" ? "Great!" : "Try Again"}
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold text-gray-900">
              Pay Installment
            </h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              ✕
            </button>
          </div>

          {loading ? (
            <div className="text-center py-4">Loading...</div>
          ) : !details?.canPay ? (
            <div className="text-center py-4">
              <p className="text-red-600">
                This installment cannot be paid at this time.
              </p>
              <button
                onClick={onClose}
                className="mt-4 px-4 py-2 bg-gray-300 text-gray-700 rounded"
              >
                Close
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              {error && (
                <div className="text-red-600 text-sm bg-red-50 p-2 rounded">
                  {error}
                </div>
              )}

              {/* Installment Details */}
              <div className="bg-gray-50 p-4 rounded">
                <h4 className="font-semibold mb-2">Payment Details</h4>
                <p>
                  <strong>Course:</strong> {details.courseName}
                </p>
                <p>
                  <strong>Student:</strong> {details.studentName}
                </p>
                <p>
                  <strong>Installment:</strong> #{details.sequenceNumber}
                </p>
                <p>
                  <strong>Amount:</strong> {formatBDT(details.amount)} BDT
                </p>
                <p>
                  <strong>Due Date:</strong>{" "}
                  {new Date(details.dueDate).toLocaleDateString()}
                </p>
              </div>

              {/* Payment Method */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Payment Method
                </label>
                <div className="flex gap-4">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      value="bkash"
                      checked={paymentMethod === "bkash"}
                      onChange={() => setPaymentMethod("bkash")}
                      className="mr-2"
                    />
                    bKash
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      value="bank"
                      checked={paymentMethod === "bank"}
                      onChange={() => setPaymentMethod("bank")}
                      className="mr-2"
                    />
                    Bank Transfer
                  </label>
                </div>
              </div>

              {/* Payment Fields */}
              {paymentMethod === "bkash" ? (
                <input
                  type="text"
                  placeholder="bKash Transaction ID *"
                  className="w-full p-3 border rounded-lg"
                  value={paymentData.transactionId}
                  onChange={(e) =>
                    setPaymentData({
                      ...paymentData,
                      transactionId: e.target.value,
                    })
                  }
                  required
                />
              ) : (
                <>
                  <input
                    type="text"
                    placeholder="Bank Name *"
                    className="w-full p-3 border rounded-lg"
                    value={paymentData.bankName}
                    onChange={(e) =>
                      setPaymentData({
                        ...paymentData,
                        bankName: e.target.value,
                      })
                    }
                    required
                  />
                  <input
                    type="text"
                    placeholder="Branch Name"
                    className="w-full p-3 border rounded-lg"
                    value={paymentData.branchName}
                    onChange={(e) =>
                      setPaymentData({
                        ...paymentData,
                        branchName: e.target.value,
                      })
                    }
                  />
                  <input
                    type="text"
                    placeholder="Reference Number *"
                    className="w-full p-3 border rounded-lg"
                    value={paymentData.referenceNumber}
                    onChange={(e) =>
                      setPaymentData({
                        ...paymentData,
                        referenceNumber: e.target.value,
                      })
                    }
                    required
                  />
                </>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Payment Date *
                </label>
                <input
                  type="date"
                  className="w-full p-3 border rounded-lg"
                  value={paymentData.paymentDate}
                  onChange={(e) =>
                    setPaymentData({
                      ...paymentData,
                      paymentDate: e.target.value,
                    })
                  }
                  required
                />
              </div>

              {/* FILE UPLOAD - MANDATORY */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Payment Slip (Required) *
                </label>
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-4">
                  <input
                    type="file"
                    onChange={handleFileChange}
                    accept=".pdf,.jpg,.jpeg,.png"
                    className="hidden"
                    id="installment-payment-slip"
                    required
                  />
                  <label
                    htmlFor="installment-payment-slip"
                    className="cursor-pointer block text-center"
                  >
                    {paymentSlip ? (
                      <div className="text-green-600">
                        <svg
                          className="mx-auto h-8 w-8 mb-1"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                            clipRule="evenodd"
                          />
                        </svg>
                        <p className="text-sm font-medium">
                          {paymentSlip.name}
                        </p>
                        <p className="text-xs text-gray-500">
                          {(paymentSlip.size / 1024).toFixed(2)} KB
                        </p>
                      </div>
                    ) : (
                      <div className="text-gray-600">
                        <svg
                          className="mx-auto h-8 w-8 mb-1"
                          stroke="currentColor"
                          fill="none"
                          viewBox="0 0 48 48"
                        >
                          <path
                            d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                        <p className="text-sm">Upload Payment Slip</p>
                        <p className="text-xs text-gray-500">
                          PDF, JPG, PNG (Max 2MB)
                        </p>
                      </div>
                    )}
                  </label>
                </div>
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={onClose}
                  className="flex-1 px-4 py-2 text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting || !paymentSlip}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {submitting ? "Submitting..." : "Submit Payment"}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default InstallmentPaymentModal;
